import {
	Activity,
	Baby,
	Bandage,
	Bug,
	Clock,
	CupSoda,
	Droplets,
	Ear,
	Eye,
	HeartPulse,
	Leaf,
	Recycle,
	ShieldCheck,
	Sparkles,
	SprayCan,
	Wind,
	type LucideIcon
} from "lucide-react"
import { imageBaseURL } from "@/lib/utils"
import type { ProductType } from "./products"

/**
 * Contenido rico (marketing) de cada producto, separado de los datos de comercio
 * de `products.ts` — mismo patrón que `treatments-content.ts` vs `treatments.ts`.
 * El texto proviene de biosany.com/productos/:slug (el de `te` es generado, pues
 * el sitio no tiene ficha para ese producto). Las imágenes son placeholders que
 * ya existen en `public/images/`, resueltas con `imageBaseURL` (nunca rutas
 * absolutas, para no romper en previews bajo subruta).
 */

/** Una ventaja del producto: icono lucide + título + texto corto. */
export interface ProductAdvantage {
	icon: LucideIcon
	title: string
	text: string
}

/**
 * Párrafo de la descripción. Cada bloque se muestra como una columna (imagen,
 * título y texto), por lo que `heading` debe estar presente para dar título a
 * su columna.
 */
export interface ProductDescriptionBlock {
	image: string
	heading: string
	body: string
}

/** Ítem del acordeón de características (imagen + título + texto). */
export interface ProductFeature {
	image: string
	title: string
	text: string
}

export interface ProductContent {
	slogan?: string
	description: ProductDescriptionBlock[]
	advantages: ProductAdvantage[]
	cta: { title: string; text?: string }
}

const prodImg = (file: string): string => imageBaseURL(`products/${file}`)

// Texto común del CTA (entrega a domicilio).
const CTA_TEXT = "Recíbelo en la puerta de tu casa de 1 a 3 días hábiles"

const CONTENT: Record<ProductType, ProductContent> = {
	"shalosh-bayom": {
		slogan: "El producto base de la bioestimulación celular",
		description: [
			{
				image: prodImg("shalosh-1.webp"),
				heading: "Tecnología de avanzada",
				body: "Shalósh Bayóm es una solución súper oxidante con un valor Redox de +900, prácticamente sin olor ni sabor. Actúa mediante un cambio de pH ácido que neutraliza virus, bacterias, esporas, hongos y radicales libres, dando soporte a tu salud desde el nivel celular."
			},
			{
				image: prodImg("shalosh-2.webp"),
				heading: "Cómo actúa",
				body: "Tres dosis diarias equivalen a una semana de tratamiento farmacéutico, sin efectos secundarios. En la primera semana elimina los microorganismos que causan la enfermedad; en la segunda repara el daño; y en la tercera y cuarta protege y rehabilita el organismo."
			}
		],
		advantages: [
			{ icon: Droplets, title: "Solución súper oxidante", text: "Valor Redox de +900 que actúa por cambio de pH ácido." },
			{ icon: Bug, title: "Neutraliza patógenos", text: "Virus, bacterias, esporas, hongos y radicales libres." },
			{ icon: Clock, title: "Acción por etapas", text: "Elimina, repara y rehabilita en menos de cuatro semanas." },
			{ icon: Sparkles, title: "Sin olor ni sabor", text: "Fácil de tomar en tu día a día." },
			{ icon: ShieldCheck, title: "Sin efectos secundarios", text: "No es un medicamento farmacéutico." },
			{ icon: Baby, title: "Para todas las edades", text: "Desde bebés hasta adultos mayores." }
		],
		cta: { title: "Empieza hoy con Shalósh Bayóm", text: CTA_TEXT }
	},

	dermogel: {
		slogan: "Gel rico en oxígeno",
		description: [
			{
				image: prodImg("dermogel-1.webp"),
				heading: "Gel rico en oxígeno",
				body: "DermoGel es el único gel del mercado elaborado a base de una solución activada rica en oxígeno. Su efecto súper oxidante sobre la piel acelera la reparación de los tejidos más rápido que el proceso natural."
			},
			{
				image: prodImg("dermogel-2.webp"),
				heading: "Cuidado de la piel",
				body: "Se aplica de forma tópica para una acción prolongada sobre la piel dañada. Es hipoalergénico y, al no ser un medicamento, puede usarse junto con protector solar y cremas humectantes."
			}
		],
		advantages: [
			{ icon: Sparkles, title: "Efecto súper oxidante", text: "Repara tu piel más rápido." },
			{ icon: Bandage, title: "Acelera la cicatrización", text: "Calma la irritación, comezón e inflamación." },
			{ icon: HeartPulse, title: "Mejora la circulación", text: "Alivia várices y cansancio en las piernas." },
			{
				icon: Activity,
				title: "Alivia dolores",
				text: "Musculares y de articulaciones, tanto por lesiones como por la edad."
			},
			{ icon: Leaf, title: "Hipoalergénico", text: "Compatible con cremas y protector solar." },
			{ icon: Baby, title: "Para todas las edades", text: "Apto para toda la familia." }
		],
		cta: { title: "Cuida tu piel con DermoGel", text: CTA_TEXT }
	},

	taftefet: {
		slogan: "Alivia tus sentidos de la vista, olfato y oídos",
		description: [
			{
				image: prodImg("taftefet-1.webp"),
				heading: "Gotas para ojos, oídos y nariz",
				body: "Taftéfet es una solución en gotas activada electro-químicamente (AEQ) y de pH neutro para ojos, oídos y nariz. Especialista en regenerar las membranas oculares y timpánicas, restaura capacidades básicas y acelera la cicatrización."
			},
			{
				image: prodImg("taftefet-2.webp"),
				heading: "Recuperación segura",
				body: "Calma molestias y dolores posquirúrgicos, limpia y protege de microorganismos, y ofrece resultados óptimos tras trasplantes de córnea, con una mejoría notable desde el primer día."
			}
		],
		advantages: [
			{ icon: Eye, title: "Regenera la vista", text: "Repara membranas oculares y acelera la cicatrización." },
			{ icon: Ear, title: "Cuida tus oídos", text: "Alivia dolores de oído y mareos." },
			{ icon: Wind, title: "Libera la nariz", text: "Resfriados, alergias y secreciones." },
			{ icon: ShieldCheck, title: "pH neutro", text: "Limpia y protege sin irritar." },
			{ icon: Bandage, title: "Alivio posquirúrgico", text: "Calma molestias tras las cirugías." },
			{ icon: Sparkles, title: "Mejoría desde el día 1", text: "Resultados notables desde el primer día." }
		],
		cta: { title: "Recupera tus sentidos con Taftéfet", text: CTA_TEXT }
	},

	"agua-vita": {
		slogan: "Agua alcalina rica en antioxidantes",
		description: [
			{
				image: prodImg("vita-1.webp"),
				heading: "Agua alcalina antioxidante",
				body: "Agua Vita es agua alcalina rica en antioxidantes, activada electro-químicamente para proteger tus células de bacterias, virus, esporas y radicales libres. Gracias a múltiples filtros de purificación es verdaderamente pura, sin olor ni sabor."
			},
			{
				image: prodImg("vita-2.webp"),
				heading: "Equilibrio y vitalidad",
				body: "Favorece la producción de oxígeno en el cuerpo, corrige el balance ácido/alcalino y ayuda a regenerar las células, combatiendo los signos del envejecimiento y haciendo más eficientes los procesos metabólicos."
			}
		],
		advantages: [
			{ icon: Droplets, title: "Agua alcalina pura", text: "Sin olor ni sabor, hidrata mejor que el agua potable." },
			{
				icon: Sparkles,
				title: "Rica en antioxidantes",
				text: "Combate los radicales libres, el cuerpo ahorra más energía para procesarla."
			},
			{
				icon: Recycle,
				title: "Equilibra tu pH",
				text: "Corrige el balance ácido/alcalino del cuerpo, mejorando la producción de oxígeno."
			},
			{
				icon: Activity,
				title: "Metabolismo eficiente",
				text: "Ayuda a eliminar grasas y toxinas, mejorando tu bienestar general."
			},
			{
				icon: Leaf,
				title: "Regenera tus células",
				text: "Retrasa los signos del envejecimiento."
			},
			{ icon: HeartPulse, title: "Cuida tu salud", text: "Mejora notablemente la circulación, riñones y digestión." }
		],
		cta: { title: "Hidrátate con Agua Vita", text: CTA_TEXT }
	},

	lazihum: {
		slogan: "Spray que elimina y previene infecciones",
		description: [
			{
				image: prodImg("lazihum-1.webp"),
				heading: "Poderosa acción en spray",
				body: "Lazihúm es una solución súper oxidante en spray con pH ligeramente ácido y poderosa acción fungicida y microbicida. Erradica infecciones con rapidez y elimina los malos olores corporales."
			},
			{
				image: prodImg("lazihum-2.webp"),
				heading: "Cuidado de la piel y femenino",
				body: "Previene y detiene infecciones en quemaduras y heridas expuestas, activa la cicatrización y combate distintos tipos de hongos. Potencia la regeneración de tejidos dañados para el cuidado íntimo."
			}
		],
		advantages: [
			{ icon: SprayCan, title: "Práctico en spray", text: "Poderosa acción de aplicación directa." },
			{ icon: Bug, title: "Fungicida y microbicida", text: "Erradica infecciones y distintos hongos." },
			{ icon: Bandage, title: "Activa la cicatrización", text: "En quemaduras y heridas expuestas." },
			{ icon: Sparkles, title: "Elimina malos olores", text: "En axilas, ingles y pies." },
			{ icon: HeartPulse, title: "Cuidado femenino", text: "Regenera tejidos y alivia molestias." },
			{ icon: Baby, title: "Para todas las edades", text: "Seguro para toda la familia." }
		],
		cta: { title: "Protégete con Lazihúm", text: CTA_TEXT }
	},

	nazelet: {
		slogan: "El especialista en sinusitis",
		description: [
			{
				image: prodImg("nazelet-1.webp"),
				heading: "Especialista en sinusitis",
				body: "Nazélet es un tratamiento súper oxidante de pH ácido contra la sinusitis. Una sola ampolleta rinde de tres a cinco lavados paranasales y regenera por completo las mucosas."
			},
			{
				image: prodImg("nazelet-2.webp"),
				heading: "Vías respiratorias libres",
				body: "Elimina los microorganismos de las membranas celulares de las vías respiratorias y las desinflama, haciéndolas más resistentes a los cambios bruscos de temperatura mediante cargas positivas de Redox."
			}
		],
		advantages: [
			{ icon: Wind, title: "Libera la respiración", text: "Sinusitis, rinitis y distintas alergias." },
			{ icon: Droplets, title: "Lavado paranasal", text: "De 3 a 5 lavados por ampolleta." },
			{ icon: Recycle, title: "Regenera las mucosas", text: "Desinflama las vías respiratorias." },
			{ icon: ShieldCheck, title: "Más resistencia", text: "Ante los cambios bruscos de temperatura." },
			{ icon: Bug, title: "Elimina microorganismos", text: "En las membranas de las vías respiratorias." },
			{ icon: Clock, title: "Tratamiento de 8 semanas", text: "Dos aplicaciones al día." }
		],
		cta: { title: "Respira mejor con Nazélet", text: CTA_TEXT }
	},

	"jabon-neutro": {
		slogan: "Jabón líquido de pH neutro",
		description: [
			{
				image: prodImg("jabon-1.webp"),
				heading: "pH neutro antibacterial",
				body: "Jabón líquido de pH neutro con agentes antibacteriales y humectantes que brinda una agradable sensación de suavidad. Ideal para preparar la piel antes de cualquier curación, con acción microbicida y pureza inmediata."
			},
			{
				image: prodImg("jabon-3.webp"),
				heading: "Limpieza y desinfección",
				body: "Su fórmula balanceada no irrita y es apta para piel seca, grasa o mixta. Limpia y desinfecta en un solo paso, dejando la piel libre de maquillaje, bloqueador y cremas, y limpia heridas de todo tipo."
			}
		],
		advantages: [
			{ icon: Droplets, title: "pH neutro", text: "Antibacterial y humectante para tu piel." },
			{ icon: Sparkles, title: "Limpia y desinfecta", text: "En un solo paso, con pureza inmediata." },
			{ icon: Bandage, title: "Prepara la piel", text: "Para curaciones y limpieza de heridas." },
			{ icon: Leaf, title: "No irrita", text: "Apto para piel seca, grasa o mixta." },
			{ icon: ShieldCheck, title: "Acción microbicida", text: "Elimina bacterias al instante." },
			{ icon: Baby, title: "Para todas las edades", text: "Apto para toda la familia." }
		],
		cta: { title: "Cuida tu piel con Jabón Neutro", text: CTA_TEXT }
	},

	te: {
		slogan: "Tés e infusiones sanadoras",
		description: [
			{
				image: prodImg("te-1.webp"),
				heading: "Tés e infusiones naturales",
				body: "Nuestros tés e infusiones combinan plantas cuidadosamente seleccionadas para acompañar tus tratamientos de bioestimulación celular. Una forma natural y reconfortante de apoyar tu bienestar día con día."
			},
			{
				image: prodImg("te-2.webp"),
				heading: "Un ritual natural",
				body: "Prepara una taza caliente para relajarte y favorecer la digestión, la hidratación y el equilibrio de tu cuerpo. Ideal para cualquier momento del día, como complemento de tu rutina de salud."
			}
		],
		advantages: [
			{ icon: Leaf, title: "100% natural", text: "Plantas seleccionadas, sin aditivos." },
			{ icon: CupSoda, title: "Complementa tu tratamiento", text: "Acompaña la bioestimulación celular." },
			{ icon: HeartPulse, title: "Favorece la digestión", text: "Reconfortante y equilibrante." },
			{ icon: Sparkles, title: "Un ritual diario", text: "Tu momento de bienestar y calma." },
			{ icon: Droplets, title: "Ayuda a la hidratación", text: "Acompaña tu día con día." },
			{ icon: Clock, title: "Para cualquier momento", text: "Por la mañana, tarde o noche." }
		],
		cta: { title: "Descubre nuestros tés e infusiones", text: CTA_TEXT }
	}
}

export const getProductContent = (id: ProductType): ProductContent => CONTENT[id]

export default CONTENT
